import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DragDropService } from '@app/core/services/drag-drop.service';
import { slideDownAnimation } from '@app/core/animations';
import {
  AddConfigAsset,
  AddNewStream,
  DeleteStream,
  RemoveImage,
  ReorderStreams,
  SortStreamsAlphabetically,
  UpdateAppConfig,
} from '@app/features/app-content/states/app-configuration.actions';
import { AppConfigurationState } from '@app/features/app-content/states/app-configuration.state';
import { StreamApi } from '@app/features/app-content/models/app-config.model';
import { Store } from '@ngxs/store';
import { filter, map, take } from 'rxjs';
import { StreamDeleteDialogComponent } from '../stream-delete-dialog/stream-delete-dialog.component';
import { StreamSortDialogComponent } from '../stream-sort-dialog/stream-sort-dialog.component';
import {
  StreamEditModalComponent,
  StreamEditModalData,
  StreamEditModalResult,
} from '../stream-edit-modal/stream-edit-modal.component';

@Component({
  selector: 'rk-stream-list',
  templateUrl: './stream-list.component.html',
  styleUrl: './stream-list.component.scss',
  animations: [slideDownAnimation],
  standalone: false,
})
export class StreamListComponent {
  private readonly store = inject(Store);
  private readonly dialog = inject(MatDialog);
  private readonly dragDropService = inject(DragDropService);

  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement>;

  isDragging = false;
  showSearch = false;
  searchQuery = '';

  currentAppConfigStreamList$ = this.store.select(
    AppConfigurationState.currentAppConfigStreamList,
  );

  get filteredStreams$() {
    return this.currentAppConfigStreamList$.pipe(
      map(streams => {
        if (!this.searchQuery) {
          return streams;
        }
        const q = this.searchQuery.toLowerCase();

        return streams.filter(
          s =>
            s.nomFlux?.toLowerCase().includes(q) ||
            s.urlFlux?.toLowerCase().includes(q),
        );
      }),
    );
  }

  onAdd(): void {
    this.currentAppConfigStreamList$.pipe(take(1)).subscribe(streams => {
      this.openModal({ existingStreams: streams });
    });
  }

  toggleSearch(): void {
    this.showSearch = !this.showSearch;
    if (this.showSearch) {
      setTimeout(() => this.searchInput?.nativeElement.focus());
    } else {
      this.searchQuery = '';
    }
  }

  sortAlphabetically(): void {
    const dialogRef = this.dialog.open(StreamSortDialogComponent, {
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.store.dispatch(new SortStreamsAlphabetically());
      }
    });
  }

  onDragStarted(): void {
    this.isDragging = true;
    this.dragDropService.startDragging();
  }

  onDrop(event: CdkDragDrop<StreamApi[]>): void {
    this.isDragging = false;
    this.dragDropService.stopDragging();
    if (event.previousIndex !== event.currentIndex) {
      this.store.dispatch(new ReorderStreams(event.previousIndex, event.currentIndex));
    }
  }

  onEdit(stream: StreamApi): void {
    this.currentAppConfigStreamList$.pipe(take(1)).subscribe(streams => {
      const index = streams.indexOf(stream);
      this.openModal({ stream, index, existingStreams: streams });
    });
  }

  onMoveToTop(stream: StreamApi): void {
    this.currentAppConfigStreamList$.pipe(take(1)).subscribe(streams => {
      const index = streams.indexOf(stream);
      this.store.dispatch(new ReorderStreams(index, 0));
    });
  }

  onMoveToBottom(stream: StreamApi): void {
    this.currentAppConfigStreamList$.pipe(take(1)).subscribe(streams => {
      const index = streams.indexOf(stream);
      this.store.dispatch(new ReorderStreams(index, streams.length - 1));
    });
  }

  onDelete(stream: StreamApi): void {
    const dialogRef = this.dialog.open(StreamDeleteDialogComponent, {
      autoFocus: false,
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.currentAppConfigStreamList$.pipe(take(1)).subscribe(streams => {
          const index = streams.indexOf(stream);
          this.store.dispatch(new DeleteStream(index));
        });
      }
    });
  }

  private openModal(data?: StreamEditModalData): void {
    const dialogRef = this.dialog.open(StreamEditModalComponent, {
      data: data || {},
      panelClass: 'stream-edit-modal-panel',
      autoFocus: false,
    });

    dialogRef
      .afterClosed()
      .pipe(filter((result): result is StreamEditModalResult => !!result))
      .subscribe(result => {
        if (data?.stream != null && data?.index != null) {
          this.applyEdit(data.index, result);
        } else {
          this.applyAdd(result);
        }
      });
  }

  private applyAdd(result: StreamEditModalResult): void {
    // add default stream then update with modal data
    this.store.dispatch(new AddNewStream());

    this.currentAppConfigStreamList$.pipe(take(1)).subscribe(streams => {
      const index = streams.length - 1;
      this.applyStreamFields(index, result);
    });
  }

  private applyEdit(index: number, result: StreamEditModalResult): void {
    this.applyStreamFields(index, result);
  }

  private applyStreamFields(index: number, result: StreamEditModalResult): void {
    const s = result.stream;
    const path = (field: string) => ['streams', index.toString(), field];

    this.store.dispatch(new UpdateAppConfig(path('nomFlux'), s.nomFlux));
    this.store.dispatch(new UpdateAppConfig(path('typeFlux'), s.typeFlux));
    this.store.dispatch(new UpdateAppConfig(path('urlFlux'), s.urlFlux));
    this.store.dispatch(new UpdateAppConfig(path('urlTitrage'), s.urlTitrage));
    this.store.dispatch(new UpdateAppConfig(path('typeTitrage'), s.typeTitrage));
    this.store.dispatch(new UpdateAppConfig(path('mountPoint'), s.mountPoint));
    this.store.dispatch(new UpdateAppConfig(path('idradio'), s.idradio));

    if (result.logoFile) {
      this.store.dispatch(
        new AddConfigAsset(
          `logoflux${index}appfield`,
          result.logoFile,
          result.logoDisplayUrl,
        ),
      );
      this.store.dispatch(
        new UpdateAppConfig(path('logofluxappfield'), result.logoDisplayUrl),
      );
    } else if (s.logofluxappfield === null && result.logoDisplayUrl === null) {
      this.store.dispatch(new RemoveImage(`logoflux${index}appfield`));
      this.store.dispatch(new UpdateAppConfig(path('logofluxappfield'), null));
    }
  }
}
